import { UserRegionEnum } from '@ad/ad_utils/src/businessRelated/ttcx_brief/type';
import {
  AddOnCategory,
  AddOnOfficialPriceTypeEnum,
  AddOnTypeEnum,
  PartnerSOWRateCard,
  ServicePackageRateCardInfo,
  StarlingText
} from '@ad/ad_utils_react/src/components/business/TTCX/AddOn/types';
import { UserRoleEnum } from '@ad/creative-business-components/src/components/Insights/types';
import { CardType } from '..';
import { AddOnTableTitleMap, BusinessType } from '@/containers/BusinessRelatedParams/consts';
import { TFunction, t } from 'i18next';
import { formatCurrency } from '@ad/ad_utils_react/src/components/business/TTCX/AddOn/helper';
import { Trans } from 'react-i18next';
import './index.module.scss';
import React from 'react';
import { UserRegionEnumMap } from '@/containers/BusinessRelatedParams/typings';
import DOMPurify from 'dompurify';

function convertT(
  tFunc: TFunction<'translation', undefined>,
  content: StarlingText | undefined,
  isStyle = false
) {
  return isStyle ? (
    <Trans
      t={tFunc}
      i18nKey={content?.starlingKey}
      defaults={content?.fallbackText}
      components={{
        br: <br />,
        b: <b />
      }}
    />
  ) : (
    tFunc(content?.starlingKey || 'error', content?.fallbackText)
  );
}

function PackageTableSchema(props: {
  cardType: CardType;
  region: UserRegionEnum;
  showRegion?: boolean;
  userRole?: UserRoleEnum;
  netNewPackages?: ServicePackageRateCardInfo[];
  repurposePackages?: ServicePackageRateCardInfo[];
  addOns?: AddOnCategory[];
  regionTextMap: UserRegionEnumMap;
  isCLP?: boolean;
}) {
  const {
    cardType,
    region,
    showRegion = true,
    userRole,
    netNewPackages = [],
    repurposePackages = [],
    addOns,
    regionTextMap,
    isCLP = false
  } = props;

  const suffix = isCLP ? ' (CLP)' : ' (VCP)';

  return (
    <div className="packages-table">
      {showRegion && (
        <div className="packages-table-region">{t(`ttcx_region_${regionTextMap[region]}`) + suffix}</div>
      )}
      <div className="packages-table-content">
        <table>
          <tbody>
            {((netNewPackages?.length ?? 0) >= 1 || (repurposePackages?.length ?? 0) >= 1) && (
              <tr>
                <td>{t('exhibit_rate_card_header_service_type', 'Service Type')}</td>
                {netNewPackages.length > 0 && (
                  <td colSpan={netNewPackages.length} key={'net-new'}>
                    {t('net_new', 'Net-New')}
                  </td>
                )}
                {repurposePackages.length > 0 && (
                  <td colSpan={repurposePackages.length} key={'repurpose'}>
                    {t('remix_project_title', 'Remix')}
                  </td>
                )}
              </tr>
            )}
            {region === UserRegionEnum.SEA ? (
              <>
                <tr>
                  <td rowSpan={2}>{t('exhibit_rate_card_header_rate_base', 'Rate base')}</td>
                  {netNewPackages?.map((item) =>
                    item?.rateBase?.fallbackText === 'Activation' ? (
                      <td
                        colSpan={2}
                        key={item?.rateBase?.starlingKey}
                        className="package-table-sea-rate-base"
                      >
                        {convertT(t, item?.rateBase)}
                      </td>
                    ) : (
                      item?.rateBase?.fallbackText !== 'Activation-D' && (
                        <td
                          rowSpan={2}
                          key={item?.rateBase?.fallbackText}
                          className="package-table-sea-rate-base"
                        >
                          {convertT(t, item?.rateBase)}
                        </td>
                      )
                    )
                  )}
                  {repurposePackages?.map((item) => (
                    <td
                      rowSpan={2}
                      key={item?.rateBase?.fallbackText}
                      className="package-table-sea-rate-base"
                    >
                      {convertT(t, item?.rateBase)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="package-table-sea-rate-base">1st Time</td>
                  <td className="package-table-sea-rate-base">Non 1st Time</td>
                </tr>
              </>
            ) : (
              <tr>
                <td>{t('exhibit_rate_card_header_rate_base', 'Rate base')}</td>
                {netNewPackages?.map((item) => (
                  <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
                ))}
                {repurposePackages?.map((item) => (
                  <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
                ))}
              </tr>
            )}
            {cardType === CardType.rateCard && (
              <tr>
                <td>{t('exhibit_rate_card_header_price', 'Price')}</td>
                {netNewPackages?.map((item) => (
                  <td key={item?.price}>
                    {`${(item?.price || 0).toLocaleString('en-US', {
                      style: 'currency',
                      currency: item?.currency || 'USD',
                      minimumFractionDigits: 0
                    })} ${item?.currency || 'USD'}`}
                    {region === UserRegionEnum.NA && item?.servicePackageId === '7231918105344360449'
                      ? t('starter_price_tip', ' if no script provided / $250 if script provided')
                      : ''}
                  </td>
                ))}
                {repurposePackages?.map((item) => (
                  <td key={item?.price}>
                    {`${(item?.price || 0).toLocaleString('en-US', {
                      style: 'currency',
                      currency: item?.currency || 'USD',
                      minimumFractionDigits: 0
                    })} ${item?.currency || 'USD'}`}
                  </td>
                ))}
              </tr>
            )}
            <tr>
              <td>{t('exhibit_rate_card_header_assets', 'Assets')}</td>
              {netNewPackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.assets, true)}</p>
                </td>
              ))}
              {repurposePackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.assets, true)}</p>
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('exhibit_rate_card_header_edits', 'Edits')}</td>
              {netNewPackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.edits, true)}</p>
                </td>
              ))}
              {repurposePackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.edits, true)}</p>
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('exhibit_rate_card_header_talents', 'Talents')}</td>
              {netNewPackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>{convertT(t, item?.talents)}</td>
              ))}
              {repurposePackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>{convertT(t, item?.talents)}</td>
              ))}
            </tr>
            {!isCLP && (
              <tr>
                <td>
                  {t('exhibit_rate_card_header_expected_asset_delivery_date', 'Expected Asset Delivery Date')}
                </td>
                {netNewPackages?.map((item, index) => {
                  return <td key={index}>{convertT(t, item?.turnaroundTime, true)}</td>;
                })}
                {repurposePackages?.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={index}>{convertT(t, item?.turnaroundTime, true)}</td>
                ))}
              </tr>
            )}
            {netNewPackages?.[0]?.minSpendRequirement && cardType === CardType.ttcxPackage && (
              <tr>
                <td>{t('exhibit_rate_card_header_minimum_spend', 'Minimum Spend')}</td>
                {netNewPackages?.map((item) => (
                  <td key={item?.minSpendRequirement?.starlingKey}>
                    {convertT(t, item?.minSpendRequirement, true)}
                  </td>
                ))}
                {repurposePackages?.map((item) => (
                  <td key={item?.minSpendRequirement?.starlingKey}>
                    {convertT(t, item?.minSpendRequirement, true)}
                  </td>
                ))}
              </tr>
            )}
            {cardType === CardType.ttcxPackage && netNewPackages?.some((item) => item?.supportSubscription) && (
              <tr>
                <td>{t('exhibit_rate_card_header_subscription_supported', 'Subscription supported')}</td>
                {netNewPackages?.map((item, idx) => (
                  <td key={`${idx}a`}>
                    {item?.supportSubscription ? t('table_yes', 'Yes') : t('table_no', 'No')}
                  </td>
                ))}
                {repurposePackages?.map((item, idx) => (
                  <td key={`${idx}b`}>{t('table_no', 'No')}</td>
                ))}
              </tr>
            )}
            {cardType === CardType.ttcxPackage &&
              netNewPackages?.some((item) => item?.supportSubscription) &&
              netNewPackages?.some((item) => item?.subscriptionPackageMinimumSpendDiscountAmount) && (
                <tr>
                  <td>
                    {t(
                      'exhibit_rate_card_header_subscription_minimum_spend_discount',
                      'Subscription minimum spend discount'
                    )}
                  </td>
                  {netNewPackages?.map((item, idx) => (
                    <td key={`${idx}a`}>
                      {(() => {
                        if (item?.subscriptionPackageMinimumSpendDiscountAmount) {
                          return `${item.subscriptionPackageMinimumSpendDiscountAmount.toLocaleString(
                            'en-US',
                            {
                              style: 'currency',
                              currency: item?.currency || 'USD',
                              minimumFractionDigits: 0
                            }
                          )} ${item?.currency || 'USD'}`;
                        } else {
                          return t('table_n_a', 'N/A');
                        }
                      })()}
                    </td>
                  ))}
                  {repurposePackages?.map((item, idx) => (
                    <td key={`${idx}b`}>{t('table_n_a', 'N/A')}</td>
                  ))}
                </tr>
              )}
            {region === UserRegionEnum.NA && cardType === CardType.ttcxPackage && (
              <tr>
                <td>
                  {t('exhibit_rate_card_header_affiliated_media_service_fee', 'Affiliated media service fee')}
                </td>
                {netNewPackages?.map((item, idx) => (
                  <td key={`${idx}a`}>
                    {item?.affiliatedMediaFee
                      ? `${(item?.affiliatedMediaFee || 0).toLocaleString('en-US', {
                          style: 'currency',
                          currency: item?.currency || 'USD',
                          minimumFractionDigits: 0
                        })} ${item?.currency || 'USD'}`
                      : '--'}
                  </td>
                ))}
                {repurposePackages?.map((item, idx) => (
                  <td key={`${idx}b`}>
                    {item?.affiliatedMediaFee
                      ? `${(item?.affiliatedMediaFee || 0).toLocaleString('en-US', {
                          style: 'currency',
                          currency: item?.currency || 'USD',
                          minimumFractionDigits: 0
                        })} ${item?.currency || 'USD'}`
                      : '--'}
                  </td>
                ))}
              </tr>
            )}
            {region === UserRegionEnum.NA && cardType === CardType.ttcxPackage && (
              <tr>
                <td>
                  {t(
                    'exhibit_rate_card_header_additional_advertiser_responsibilities',
                    'Additional Advertiser Responsibilities'
                  )}
                </td>
                {region === UserRegionEnum.NA && cardType === CardType.ttcxPackage && (
                  <td colSpan={16}>
                    {
                      'Provide feedback on Partner uploads (e.g. Concepts, final deliverables) within 2 business days.'
                    }
                  </td>
                )}
              </tr>
            )}
            {region === UserRegionEnum.CNOB && cardType === CardType.ttcxPackage && (
              <tr>
                <td>Target Market</td>
                {netNewPackages?.map((item) => (
                  <td key={item?.targetMarket}>{item?.targetMarket || '-'}</td>
                ))}
                {repurposePackages?.map((item) => (
                  <td key={item?.targetMarket}>{item?.targetMarket || '-'}</td>
                ))}
              </tr>
            )}
            {[
              UserRegionEnum.LATAM_Colombia_Peru,
              UserRegionEnum.LATAM_Ecuador_SouthCone,
              UserRegionEnum.LATAM_MX_Miami
            ].includes(region) && (
              <tr>
                <td>{t('exhibit_rate_card_header_remark', 'Remark')}</td>
                <td colSpan={4}>{t('latam_mx_rate_card_remark')}</td>
              </tr>
            )}
            {region === UserRegionEnum.NA && cardType === CardType.rateCard && !isCLP && (
              <tr>
                <td>{t('exhibit_rate_card_header_remark', 'Remark')}</td>
                {netNewPackages?.map((item) => (
                  <td key={item?.remark?.starlingKey}>
                    {convertT(t, item?.remark) ? convertT(t, item?.remark) : '-'}{' '}
                  </td>
                ))}
                {repurposePackages?.map((item) => (
                  <td key={item?.remark?.starlingKey}> - </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showRegion && addOns && (
        <div className="packages-table-region">
          {`${t(`ttcx_region_${regionTextMap[region]}`)} `}
          {t('exhibit_rate_card_header_add_on_service', 'Add-on Service')}
        </div>
      )}
      {showRegion &&
        addOns &&
        addOns.map((addOn) => (
          <>
            <div className="packages-table-addOn" key={'add-on'}>
              {t(AddOnTableTitleMap[addOn.type || AddOnTypeEnum.Undefined])}
            </div>
            <div className="packages-table-content">
              <table>
                <tbody>
                  <tr className="package-table-add-on-header">
                    <td />
                    <td key={'service-code'}>{t('exhibit_rate_card_header_service_code', 'Service Code')}</td>
                    <td key={'price'}>
                      {userRole === UserRoleEnum.Partner
                        ? t('exhibit_rate_card_header_price', 'Price')
                        : t('exhibit_rate_card_header_minimum_spend', 'Minimum Spend')}
                    </td>
                    <td key={'service-description'}>
                      {t('exhibit_rate_card_header_service_description', 'Service Description')}
                    </td>
                  </tr>
                  {addOn &&
                    (addOn.serieses || []).map((series) =>
                      series.addOns?.map((addOnItem, index) => (
                        <tr key={addOnItem?.code}>
                          {index === 0 && (
                            <td rowSpan={(series?.addOns || []).length}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    `<span>${convertT(t, series?.seriesNameV2)}</span>`
                                  )
                                }}
                              />
                            </td>
                          )}
                          <td className="package-table-add-on-info">{addOnItem?.code}</td>
                          {userRole === UserRoleEnum.Partner ? (
                            addOnItem?.officialPriceType === AddOnOfficialPriceTypeEnum.Percentage ? (
                              <td className="package-table-add-on-info">{`${(
                                (addOnItem?.price || 0) * 100
                              ).toFixed()}%`}</td>
                            ) : (
                              <td className="package-table-add-on-info">{`${formatCurrency(
                                addOnItem?.price,
                                addOnItem?.currency
                              )} ${addOnItem?.currency || 'USD'} `}</td>
                            )
                          ) : (
                            <td className="package-table-add-on-info">
                              {`${formatCurrency(addOnItem?.minSpend, addOnItem?.currency)} ${
                                addOnItem?.currency || 'USD'
                              } `}
                            </td>
                          )}
                          <td className="package-table-add-on-info">
                            {convertT(t, addOnItem?.rateCardDescriptionV2, true)}
                          </td>
                        </tr>
                      ))
                    )}
                </tbody>
              </table>
            </div>
          </>
        ))}
    </div>
  );
}

export default PackageTableSchema;
